/**
 * KNK-1.11 / FR-11 — Knock Mode as an Account feature, and its readiness checklist.
 *
 * The decisions, with no React, so they can be tested. The data is `knock_mode_status()`
 * (migration 20261005a1), which answers for the caller's own account:
 *   off → { enabled: false, ready: false, items: [] }
 *   on  → { enabled: true, ready, items: [quick_buttons, location_policy, doors] }
 *   `doors` passes on Doors in the account OR an Area shared (KNK-1.15, migration 20261005b7).
 *
 * ⚠️ THIS IS NOT THE GATE. The database is: only a superadmin can switch Knock Mode
 * (`set_account_module`, KNK-1.55; it is the `knock_mode` add-on module), only `field.settings.manage` can attest an item
 * (`set_knock_readiness_item`), and `log_knock` refuses a new Knock with KM001 while off.
 * What this file decides is what to RENDER.
 *
 * ⚠️ Parsing fails closed to OFF. A malformed or unreadable answer must hide Knock Mode, not
 * show a half-configured checklist, because "off" is the state FR-11 makes safe.
 */

export type KnockReadinessKey = 'quick_buttons' | 'location_policy' | 'doors';

export const KNOCK_READINESS_KEYS: readonly KnockReadinessKey[] = ['quick_buttons', 'location_policy', 'doors'];

/** The two items a person attests to. `doors` is counted by the server and cannot be confirmed. */
export type KnockAttestedKey = Exclude<KnockReadinessKey, 'doors'>;

export interface KnockReadinessItem {
  key: KnockReadinessKey;
  passed: boolean;
  confirmedAt: string | null;
  confirmedByName: string | null;
  /** `doors` only: Doors in the whole account, not just the ones the caller reaches. */
  doorCount: number | null;
  /** `doors` only (KNK-1.15): Areas shared and not yet ended. The item passes on either. */
  sharedAreaCount: number | null;
  /** `quick_buttons` only (KNK-1.12): the Account's quick buttons, by name, in slot order. */
  buttons: string[];
  /** `location_policy` only (KNK-1.37): the current policy version, and the review of it, if any. */
  policyVersion: number | null;
  review: LocationPolicyReview | null;
}

/**
 * KNK-1.37. Counsel's review of the current location policy for this Account, or the pilot
 * exemption Allan granted Joules and Sandbox for version 1 (migration 20261009b2). The item passes
 * only with a review AND an attestation, so a new policy version re-opens it.
 */
export interface LocationPolicyReview {
  kind: 'counsel' | 'pilot_exemption';
  states: string[];
  reviewer: string | null;
  note: string | null;
  recordedAt: string | null;
  recordedByName: string | null;
}

function parseReview(v: unknown): LocationPolicyReview | null {
  if (!isRecord(v) || (v.kind !== 'counsel' && v.kind !== 'pilot_exemption')) return null;
  return {
    kind: v.kind,
    states: Array.isArray(v.states) ? v.states.filter((x): x is string => typeof x === 'string') : [],
    reviewer: str(v.reviewer),
    note: str(v.note),
    recordedAt: str(v.recorded_at),
    recordedByName: str(v.recorded_by_name),
  };
}

export interface KnockModeStatus {
  enabled: boolean;
  ready: boolean;
  items: KnockReadinessItem[];
}

export const KNOCK_MODE_OFF: KnockModeStatus = Object.freeze({ enabled: false, ready: false, items: [] }) as KnockModeStatus;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function parseKnockModeStatus(raw: unknown): KnockModeStatus {
  if (!isRecord(raw) || raw.enabled !== true || !Array.isArray(raw.items)) return KNOCK_MODE_OFF;

  const items: KnockReadinessItem[] = [];
  for (const key of KNOCK_READINESS_KEYS) {
    const found = raw.items.find((i) => isRecord(i) && i.key === key);
    if (!isRecord(found)) continue;
    items.push({
      key,
      passed: found.passed === true,
      confirmedAt: str(found.confirmed_at),
      confirmedByName: str(found.confirmed_by_name),
      doorCount: typeof found.door_count === 'number' ? found.door_count : null,
      sharedAreaCount: typeof found.shared_area_count === 'number' ? found.shared_area_count : null,
      buttons: Array.isArray(found.buttons) ? found.buttons.filter((b): b is string => typeof b === 'string') : [],
      policyVersion: typeof found.policy_version === 'number' ? found.policy_version : null,
      review: parseReview(found.review),
    });
  }

  // Ready is recomputed, never read: every item FR-11 names must be present AND passed.
  const ready = items.length === KNOCK_READINESS_KEYS.length && items.every((i) => i.passed);
  return { enabled: true, ready, items };
}

export interface KnockModeVisibilityInput {
  status: KnockModeStatus;
  loading: boolean;
  canManageField: boolean;
}

/**
 * Should Settings offer a Knock Mode panel (and the sidebar an entry for it)?
 *
 * Knock Mode on AND `field.settings.manage` (a superadmin holds it through system.override).
 * While off, nobody: since KNK-1.55 the switch is under Settings → Add-on modules, so this panel
 * has nothing to show. While loading, nobody: an entry that appears and then vanishes has already
 * told an account without Knock Mode that it exists.
 */
export function knockModeSettingsVisible({ status, loading, canManageField }: KnockModeVisibilityInput): boolean {
  if (loading) return false;
  return status.enabled && canManageField;
}

export const KNOCK_MODE_COPY = {
  title: 'Knock Mode',
  /** FR-11's sentence. joules-mobile shows reps these exact words. */
  repNotReady: 'Knock Mode is being set up',
  checklistTitle: 'Readiness checklist',
  checklistReady: 'Ready. Reps can start knocking.',
  checklistNotReady: 'Not ready. Until every item passes, reps see “Knock Mode is being set up”.',
  confirm: 'Confirm',
  /** KNK-1.37. */
  policy: {
    noReview: 'Counsel review not recorded yet. Record it before confirming.',
    pilotExemption: 'Pilot exemption: counsel review not recorded. Allowed for version 1 only; a new policy version needs counsel.',
    readText: 'Read the policy text',
    hideText: 'Hide the policy text',
    record: 'Record counsel review',
    rerecord: 'Record a new counsel review',
    statesLabel: 'States reviewed for',
    statesHint: 'Two-letter codes, separated by commas: CO, TX',
    reviewerLabel: 'Reviewed by',
    reviewerHint: 'The lawyer or firm',
    noteLabel: 'Note (optional)',
    save: 'Save review',
    cancel: 'Cancel',
  },
  withdraw: 'Withdraw',
  items: {
    quick_buttons: {
      label: 'Quick buttons confirmed',
      detail: 'The Dispositions on the Door sheet’s quick buttons, set under Quick buttons below, are the ones your reps should tap. Teams with buttons of their own use those.',
    },
    location_policy: {
      label: 'Location policy text reviewed',
      detail: 'Counsel has reviewed what reps are told about location (what is collected, who sees it and how long it is kept) for the states your reps work in, and someone accountable confirms it. Every rep agrees to it before their first Knock; no account can turn that off.',
    },
    doors: {
      label: 'Doors seeded or an area shared',
      detail: 'The account holds at least one Door, or a manager has shared an area that has not ended. This one is counted, not confirmed.',
    },
  } satisfies Record<KnockReadinessKey, { label: string; detail: string }>,
} as const;

/** KNK-1.12. What "quick buttons confirmed" refers to: the Account's buttons as they are now. */
export function buttonsLine(item: KnockReadinessItem): string | null {
  if (item.key !== 'quick_buttons') return null;
  return item.buttons.length === 0 ? 'No quick buttons are set yet.' : `Buttons: ${[...item.buttons, 'More'].join(' · ')}`;
}

export function confirmedLine(item: KnockReadinessItem): string | null {
  if (item.key === 'doors') {
    if (item.doorCount === null) return null;
    const doors = `${item.doorCount.toLocaleString()} Door${item.doorCount === 1 ? '' : 's'} in this account`;
    const areas = item.sharedAreaCount;
    return areas ? `${doors} · ${areas.toLocaleString()} area${areas === 1 ? '' : 's'} shared` : doors;
  }
  if (!item.passed) return null;
  const when = item.confirmedAt ? new Date(item.confirmedAt).toLocaleDateString() : null;
  const who = item.confirmedByName ?? 'someone no longer in this account';
  return when ? `Confirmed by ${who} on ${when}` : `Confirmed by ${who}`;
}

/** KNK-1.37. What the checklist says about counsel's review of the location policy. */
export function reviewLine(item: KnockReadinessItem): string | null {
  if (item.key !== 'location_policy') return null;
  const r = item.review;
  if (!r) return KNOCK_MODE_COPY.policy.noReview;
  if (r.kind === 'pilot_exemption') return KNOCK_MODE_COPY.policy.pilotExemption;
  const when = r.recordedAt ? ` on ${new Date(r.recordedAt).toLocaleDateString()}` : '';
  const by = r.recordedByName ? `, recorded by ${r.recordedByName}` : '';
  return `${r.reviewer ?? 'Counsel'} reviewed version ${item.policyVersion ?? '?'} for ${r.states.join(', ')}${by}${when}`;
}

/** KNK-1.37. "co, tx; NV" → ['CO', 'NV', 'TX']. The server validates and normalises the same way. */
export function parseStatesInput(raw: string): string[] {
  return [...new Set(raw.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))].sort();
}
